<script setup lang="ts">
/**
 * Role-aware navigation. One landmark, three shapes.
 *
 * DESIGN.md section 12 gives per-viewport intent rather than "stacked on mobile":
 *
 *   | 360  | bottom tab bar, 4 max        |
 *   | 768  | collapsible rail, icons      |
 *   | 1024 | persistent rail              |
 *   | 1280 | persistent rail, labels      |
 *
 * The four-item cap at 360 is why `primary` exists in the catalog: three
 * screens per role plus a More disclosure is four. Which three is a per-role
 * decision recorded in the catalog, not a slice of whatever order the list
 * happens to be in.
 *
 * Labels are always in the DOM. Below 1280 they are visually hidden rather than
 * omitted, so the accessible name of an icon-only rail item is the label itself
 * and there is no second copy of the string to fall out of sync.
 *
 * This component owns the links and nothing else. The rail's width, background
 * and full-viewport height belong to `AuthenticatedShell`, which puts the brand
 * above these links and the account panel below them, and `rail.ts` owns the
 * one decision all three share.
 */

import { computed, ref } from 'vue'

import { useRailShape } from './rail'

import MpIcon from '@/components/base/MpIcon.vue'
import { navigationFor, type ScreenDefinition } from '@/screens/catalog'
import { useSessionStore } from '@/stores/session'
import { useUiStore } from '@/stores/ui'

const session = useSessionStore()
const ui = useUiStore()
const { phone, collapsible, labeled } = useRailShape()

const items = computed<readonly ScreenDefinition[]>(() =>
  session.role ? navigationFor(session.role) : []
)

const dayToDay = computed(() => items.value.filter(s => s.nav?.group === undefined))
const adminConsole = computed(() => items.value.filter(s => s.nav?.group === 'admin-console'))

/** Three at most: the fourth tab slot belongs to More. */
const tabs = computed(() => items.value.filter(s => s.nav?.primary === true).slice(0, 3))
const overflow = computed(() => items.value.filter(s => !tabs.value.includes(s)))

const moreOpen = ref(false)
</script>

<template>
  <nav
    class="mp-nav"
    :class="[phone ? 'mp-nav--tabs' : 'mp-nav--rail', { 'mp-nav--labeled': labeled }]"
    role="navigation"
    aria-label="Main"
  >
    <!-- ── 360: bottom tab bar ────────────────────────────────────────────── -->
    <template v-if="phone">
      <ul class="mp-nav__tabs">
        <li
          v-for="screen in tabs"
          :key="screen.id"
        >
          <RouterLink
            class="mp-nav__tab mp-hit"
            :to="screen.path"
          >
            <MpIcon :name="screen.nav!.icon" />
            <span class="mp-nav__tab-label">{{ screen.nav!.label }}</span>
          </RouterLink>
        </li>
        <li v-if="overflow.length">
          <button
            class="mp-nav__tab mp-hit"
            type="button"
            aria-controls="mp-nav-more"
            :aria-expanded="moreOpen"
            @click="moreOpen = !moreOpen"
          >
            <MpIcon name="more" />
            <span class="mp-nav__tab-label">More</span>
          </button>
        </li>
      </ul>

      <div
        v-show="moreOpen"
        id="mp-nav-more"
        class="mp-nav__sheet"
      >
        <ul>
          <li
            v-for="screen in overflow"
            :key="screen.id"
          >
            <RouterLink
              class="mp-nav__sheet-link mp-hit"
              :to="screen.path"
              @click="moreOpen = false"
            >
              <MpIcon
                :name="screen.nav!.icon"
                size="sm"
              />
              <span>{{ screen.nav!.label }}</span>
            </RouterLink>
          </li>
        </ul>
      </div>
    </template>

    <!-- ── 768+: rail ─────────────────────────────────────────────────────── -->
    <template v-else>
      <button
        v-if="collapsible"
        class="mp-nav__toggle mp-hit"
        type="button"
        :aria-expanded="ui.navExpanded"
        aria-controls="mp-nav-list"
        @click="ui.setNavExpanded(!ui.navExpanded)"
      >
        <MpIcon name="more" />
        <span class="mp-sr-only">{{ ui.navExpanded ? 'Collapse navigation' : 'Expand navigation' }}</span>
      </button>

      <ul
        id="mp-nav-list"
        class="mp-nav__list"
      >
        <li
          v-for="screen in dayToDay"
          :key="screen.id"
        >
          <RouterLink
            class="mp-nav__link mp-hit"
            :to="screen.path"
          >
            <MpIcon :name="screen.nav!.icon" />
            <span :class="labeled ? 'mp-nav__label' : 'mp-sr-only'">{{ screen.nav!.label }}</span>
          </RouterLink>
        </li>
      </ul>

      <template v-if="adminConsole.length">
        <h2
          id="mp-nav-admin-heading"
          :class="labeled ? 'mp-nav__group' : 'mp-sr-only'"
        >
          Admin console
        </h2>
        <ul
          class="mp-nav__list"
          aria-labelledby="mp-nav-admin-heading"
        >
          <li
            v-for="screen in adminConsole"
            :key="screen.id"
          >
            <RouterLink
              class="mp-nav__link mp-hit"
              :to="screen.path"
            >
              <MpIcon :name="screen.nav!.icon" />
              <span :class="labeled ? 'mp-nav__label' : 'mp-sr-only'">{{ screen.nav!.label }}</span>
            </RouterLink>
          </li>
        </ul>
      </template>
    </template>
  </nav>
</template>

<style scoped>
.mp-nav ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

/* ── Rail ─────────────────────────────────────────────────────────────────── */

/* The links take the height the brand and the account panel leave, and scroll
   inside it — a role's list grows with every screen that gets built, and a
   short laptop viewport has only so many rows to give. Without `min-block-size: 0` the flex item refuses
   to shrink below its content and pushes the account panel off the bottom of
   the rail instead, which is the one thing the rail exists to prevent.
   `padding-inline` is the 4px the focus ring needs, since a scroll container
   clips at its padding edge; the negative margin gives it straight back so the
   links still line up with the brand above them. */
.mp-nav--rail {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2);
  min-block-size: 0;
  overflow-y: auto;
  padding: var(--space-1);
  margin: calc(-1 * var(--space-1));
}

.mp-nav__list {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.mp-nav__link,
.mp-nav__toggle {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-block-size: var(--target-min);
  padding: var(--space-2) var(--space-3);
  border: 1px solid transparent;
  border-radius: var(--radius-control);
  background: transparent;
  color: var(--text-on-nav);
  text-decoration: none;
  font-family: var(--font-body);
  font-size: var(--font-size-small);
  font-weight: 600;
  cursor: pointer;
  transition: background-color var(--motion-fast) var(--motion-ease);
}

.mp-nav__link:visited {
  color: var(--text-on-nav);
}

.mp-nav__link:hover,
.mp-nav__toggle:hover {
  background: color-mix(in srgb, var(--text-on-nav) 14%, transparent);
}

.mp-nav__link:focus-visible,
.mp-nav__toggle:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

/* The current screen is marked by weight and a rule as well as by fill, so it is
   not carried by color alone. DESIGN.md section 8. */
.mp-nav__link.router-link-active {
  background: color-mix(in srgb, var(--text-on-nav) 20%, transparent);
  border-color: var(--text-on-nav);
}

.mp-nav__label {
  white-space: nowrap;
}

.mp-nav__group {
  margin-block: var(--space-4) var(--space-1);
  padding-inline: var(--space-3);
  font-family: var(--font-body);
  font-size: var(--font-size-caption);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-on-nav);
  opacity: 0.75;
}

/* ── Bottom tab bar ───────────────────────────────────────────────────────── */

.mp-nav--tabs {
  position: fixed;
  inset-inline: 0;
  inset-block-end: 0;
  z-index: 40;
  background: var(--surface-nav);
  color: var(--text-on-nav);
  /* Home-indicator inset on iOS, zero elsewhere. */
  padding-block-end: env(safe-area-inset-bottom, 0px);
}

.mp-nav__tabs {
  display: flex;
}

.mp-nav__tabs > li {
  flex: 1;
}

.mp-nav__tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-1);
  inline-size: 100%;
  min-block-size: var(--target-min);
  padding-block: var(--space-2);
  border: 0;
  border-block-start: 3px solid transparent;
  background: transparent;
  color: var(--text-on-nav);
  text-decoration: none;
  font-family: var(--font-body);
  font-size: var(--font-size-caption);
  font-weight: 600;
  cursor: pointer;
}

.mp-nav__tab:visited {
  color: var(--text-on-nav);
}

.mp-nav__tab:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

.mp-nav__tab.router-link-active {
  border-block-start-color: var(--text-on-nav);
  background: color-mix(in srgb, var(--text-on-nav) 16%, transparent);
}

.mp-nav__tab-label {
  text-align: center;
  line-height: 1.2;
}

.mp-nav__sheet {
  position: absolute;
  inset-block-end: 100%;
  inset-inline: 0;
  padding: var(--space-2);
  background: var(--surface-nav);
  border-block-start: 1px solid color-mix(in srgb, var(--text-on-nav) 25%, transparent);
  box-shadow: var(--shadow-overlay);
}

.mp-nav__sheet-link {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-block-size: var(--target-min);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-control);
  color: var(--text-on-nav);
  text-decoration: none;
  font-size: var(--font-size-body);
  font-weight: 600;
}

.mp-nav__sheet-link:visited {
  color: var(--text-on-nav);
}

.mp-nav__sheet-link:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}
</style>
